<script>
  import { onMount, onDestroy } from 'svelte';
  import supabase from '../shared/supabaseClient';
  import SkillsAnalyzer from './SkillsAnalyzer.svelte';

  let user = null;
  let isInitializing = true;
  let isSubmitting = false;
  let isPremium = false;
  let resumeFile;
  let jobDescription = '';
  let error = null;
  let generatedResumeHtml = null;
  let loadingMessage = '';
  let selectedStyle = 'BasicPig';
  let showDropdown = false;
  let messageInterval;
  let showUpgradeModal = false;
  let profileContainer;
  let tags = [];
  let newTag = '';
  let useSavedResume = false;
  let hasSavedResume = false;
  let showSkillsAnalyzer = false;
  // No need for authRefreshAttempted, handled inside onAuthStateChange

  const loadingMessages = [
    "Deploying resume editing AI swarms...",
    "Extracting data...",
    "Bathing in data...",
    "Polishing your professional prowess...",
    "Aligning stars for optimal job matching...",
    "Decoding the matrix of your career...",
    "Summoning the spirits of successful applicants...",
    "Calculating the perfect word-to-awesome ratio...",
    "Injecting pure talent into your resume...",
    "Transforming your experiences into pure gold...",
    "Feeling porky..."
  ];

  const unsubscribeAuth = () => {
      if (authListener && authListener.subscription) {
          authListener.subscription.unsubscribe();
      }
  }

  let authListener; // Declare authListener outside onMount

  onMount(async () => {
    console.log("[onMount] Starting initialization...");
    document.addEventListener('click', handleClickOutside);

    const { data: { user: currentUser }, error: userError } = await supabase.auth.getUser();
    if (userError) {
      console.error("[onMount] Error getting user:", userError);
    }

    user = currentUser;

    if (user) {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.access_token) {
        // Set the auth token in a cookie
        document.cookie = `auth_token=${session.access_token}; path=/; secure; samesite=strict`;
      }
      await checkPremiumStatus(user.email);
      await loadUserTags(user.email);
      // Check if user has a saved resume
      const response = await fetch('/api/resume/has-saved', {
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        }
      });
      const data = await response.json();
      hasSavedResume = data.hasSaved;
    }

    isInitializing = false;
    console.log("[onMount] Initialization complete");

    /*if (user && window.location.search.includes('test=true')) {
      generatedResumeHtml = 'resume_1740358636999_ddm8xm84yj5.html';
      console.log("[onMount] Test mode enabled: generatedResumeHtml set to", generatedResumeHtml);
    }*/

    authListener = supabase.auth.onAuthStateChange((event, session) => {
      console.log("[AuthStateChange] Auth event received:", event);
      setTimeout(async () => {
        if (event === 'SIGNED_IN' && session?.user) {
          user = session.user;
          // Set the token in a cookie on sign-in
          document.cookie = `auth_token=${session.access_token}; path=/; secure; samesite=strict`;
          if (user?.email) {
            await checkPremiumStatus(user.email);
          }
        } else if (event === 'SIGNED_OUT') {
          user = null;
          isPremium = false;
          // Clear the cookie on sign-out
          document.cookie = "auth_token=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/";
        } else if (event === 'TOKEN_REFRESHED' && session?.access_token) {
          // Update the cookie if the token is refreshed
          document.cookie = `auth_token=${session.access_token}; path=/; secure; samesite=strict`;
        }
      }, 0);
    });
  });

  onDestroy(() => {
    document.removeEventListener('click', handleClickOutside);
      unsubscribeAuth();
  });


  async function checkPremiumStatus(email) {
    const { data: userData, error: userError } = await supabase
      .from('users')
      .select('premium, tags')
      .eq('email', email)
      .single();

    if (!userError && userData) {
      isPremium = !!userData.premium;
      console.log("[checkPremiumStatus] updated isPremium =>", isPremium);
      
      // Load tags if available
      if (userData.tags) {
        tags = Array.isArray(userData.tags) ? userData.tags : [];
        console.log("[checkPremiumStatus] loaded tags =>", tags);
      }
    } else if (userError){
        console.error("[checkPremiumStatus] Error checking premium status:", userError);
    }
  }

  async function loadUserTags(email) {
    const { data: userData, error: userError } = await supabase
      .from('users')
      .select('tags')
      .eq('email', email)
      .single();

    if (!userError && userData && userData.tags) {
      tags = Array.isArray(userData.tags) ? userData.tags : [];
      console.log("[loadUserTags] loaded tags =>", tags);
    }
  }

  async function saveUserTags() {
    if (!user) return;
    
    const { error } = await supabase
      .from('users')
      .update({ tags })
      .eq('email', user.email);
      
    if (error) {
      console.error("[saveUserTags] Error saving tags:", error);
    } else {
      console.log("[saveUserTags] Tags saved successfully");
    }
  }

  function addTag() {
    if (newTag.trim()) {
      tags = [...tags, newTag.trim()];
      newTag = '';
      saveUserTags();
    }
  }

  function removeTag(index) {
    tags = tags.filter((_, i) => i !== index);
    saveUserTags();
  }

  function handleKeydown(event) {
    if (event.key === 'Enter') {
      event.preventDefault();
      addTag();
    }
  }

  function handleClickOutside(e) {
    const profileContainer = document.querySelector('.profile-container');
    if (profileContainer && !profileContainer.contains(e.target)) {
      showDropdown = false;
    }
  }

  $: if (!isInitializing && !user) {
    console.log("[$] isInitializing:", isInitializing, "user:", user, "=> redirecting to docs");
    window.location.href = 'https://docs.resumehog.com';
  }

  function updateLoadingMessage() {
    loadingMessage = loadingMessages[Math.floor(Math.random() * loadingMessages.length)];
  }

  async function uploadToSupabase(file) {
    console.log("[uploadToSupabase] Starting upload with file:", file);
    try {
      const fileName = `resume_${Date.now()}_${Math.random().toString(36).slice(2)}.pdf`;
      const { data, error: uploadError } = await supabase.storage
        .from('pdfs')
        .upload(fileName, file);

      if (uploadError) {
        console.error("[uploadToSupabase] Upload error:", uploadError);
        throw uploadError;
      }

      console.log("[uploadToSupabase] Upload success. data:", data);
      return data.path;
    } catch (error) {
      console.error("[uploadToSupabase] Critical error:", error);
      throw error;
    }
  }

  async function handleSubmit(event) {
    event.preventDefault();
    console.log("[handleSubmit] Starting submission process");

    if (!user) {
      console.log("[handleSubmit] No authenticated user, redirecting");
      window.location.href = 'https://docs.resumehog.com';
      return;
    }

    isSubmitting = true;
    error = null;
    generatedResumeHtml = null;
    console.log("[handleSubmit] Starting submission flow.");

    try {
      if (!useSavedResume && (!resumeFile || resumeFile.length === 0)) {
        console.log("[handleSubmit] No file selected");
        throw new Error('Please select a resume file');
      }
      console.log("[handleSubmit] File selected, size:", resumeFile?.[0]?.size);

      messageInterval = setInterval(updateLoadingMessage, 3000);
      updateLoadingMessage();

      let filePath;
      let publicUrl;
      if (!useSavedResume) {
        filePath = await uploadToSupabase(resumeFile[0]);
        console.log("[handleSubmit] File upload completed");
        const { data: urlData } = supabase.storage.from('pdfs').getPublicUrl(filePath);
        publicUrl = urlData.publicUrl;
        console.log("[handleSubmit] Public URL generated");
      }

      const session = user ? (await supabase.auth.getSession()).data.session : null;
      console.log("[handleSubmit] Session status:", !!session?.access_token);

      if (!session?.access_token) {
        throw new Error('No authentication token available');
      }

      const controller = new AbortController();
      const timeoutId = setTimeout(() => {
        console.log("[handleSubmit] About to ABORT fetch due to timeout.");
        controller.abort();
      }, 180000);

      const response = await fetch('/api/resume/generate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          fileUrl: useSavedResume ? null : publicUrl,
          jobDescription,
          selectedStyle,
          userEmail: user.email,
          customTags: tags,
          useSavedResume
        }),
        signal: controller.signal
      });

      clearTimeout(timeoutId);
      console.log("[handleSubmit] API response status:", response.status);

      const data = await response.json();
      console.log("[handleSubmit] API response received");

      if (!response.ok) {
        if (data.requiresUpgrade) {
          console.log("[handleSubmit] Upgrade required");
          error = data.error;
          showUpgradeModal = true;
          return;
        }
        throw new Error(data.error || `HTTP error! status: ${response.status}`);
      }

      generatedResumeHtml = data.hogUrl;
      console.log("[handleSubmit] Resume generation successful");

    } catch (err) {
      if (err.name === 'AbortError') {
        error = 'Request timed out. Please try again.';
        console.error('[handleSubmit] AbortError -> Timeout:', err);
      } else {
        console.error('[handleSubmit] Critical error:', err);
        error = err.message || 'An unexpected error occurred';

        if (err.message.includes('invalid JWT')) {
            console.log('[handleSubmit] Invalid JWT detected, forcing logout');
            setTimeout(async () => {
              await supabase.auth.signOut();
              window.location.reload();
            }, 0);
        }
      }
    } finally {
      isSubmitting = false;
      console.log("[handleSubmit] Done. isSubmitting ->", isSubmitting);

      if (messageInterval) {
        clearInterval(messageInterval);
        messageInterval = null;
        console.log("[handleSubmit] Clearing message interval.");
      }
    }
  }

  async function openResumeInNewTab() {
    if (generatedResumeHtml) {
      const fileName = generatedResumeHtml.split('/').pop();
      // Open the resume URL directly in a new tab
      window.open(`/resume/edit-resume/${fileName}`, '_blank');
    }
  }

    async function signOut() {
      setTimeout(async () => {
        const { error } = await supabase.auth.signOut();
        if (error) console.error('Error signing out:', error);
          else {
            user = null;
            isPremium = false;
          }
      }, 0);
  }

  function toggleDropdown() {
    showDropdown = !showDropdown;
  }

  async function handleSubscribe() {
    try {
      isSubmitting = true;
      const response = await fetch('/api/stripe/create-checkout-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: user.email })
      });
      if (!response.ok) {
        throw new Error('Failed to create checkout session');
      }
      const { url: checkoutUrl } = await response.json();
      window.location.href = checkoutUrl;
    } catch (error) {
      console.error('Error initiating subscription:', error);
    } finally {
      isSubmitting = false;
    }
  }

  async function handleCancelSubscription() {
    try {
      isSubmitting = true;
      const response = await fetch('/api/stripe/cancel-subscription', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: user.email })
      });
      if (!response.ok) {
        throw new Error('Failed to cancel subscription');
      }
      alert('Your subscription will remain active until the end of the current billing period.');
    } catch (error) {
      console.error('Error cancelling subscription:', error);
      alert('Failed to cancel subscription');
    } finally {
      isSubmitting = false;
    }
  }

  function openSkillsAnalyzer() {
    if (isPremium) {
        showSkillsAnalyzer = true;
    } else {
        console.log("Skills Analyzer requires premium access.");
    }
  }
</script>

{#if isInitializing}
  <p>Loading...</p>
{:else if !user}
  <p>Please log in</p>
{:else}
  <header class="top-nav">
    <h1>Resume Hog</h1>
    <div class="user-controls">
      <div class="profile-container" bind:this={profileContainer}>
        <button class="profile-button" on:click={toggleDropdown}>
          Welcome, {user.email} ▼
        </button>

        {#if showDropdown}
          <div class="dropdown-content">
            {#if !isPremium}
              <button on:click={handleSubscribe}>Upgrade to Premium</button>
            {:else}
              <button on:click={handleCancelSubscription}>Cancel Subscription</button>
            {/if}
            <button on:click={signOut}>Sign Out</button>
          </div>
        {/if}
      </div>
    </div>
  </header>

  <main>
    <section class="how-it-works">
      <h2>How it Works</h2>
      <ol>
        <li>Attach your existing resume</li>
        <li>Give us some details about the new job you want to apply for</li>
        <li>Click Generate, sit back, relax, and let the magic happen! <span class="breathing-glow">Takes about 2 minutes.</span></li>
      </ol>
    </section>

    <div class="action-buttons-extra">
       <button
            class="skills-analyzer-button"
            on:click={openSkillsAnalyzer}
            disabled={!isPremium}
            title={!isPremium ? 'This feature is only available to premium users' : 'Analyze job descriptions against your profile'}
        >
            Skills Analyzer ✨
            {#if !isPremium}<span class="premium-lock">🔒</span>{/if}
       </button>
    </div>

    <form on:submit={handleSubmit}>
      <div class="toggle-label">
        <label>
          <input type="checkbox" bind:checked={useSavedResume} disabled={!hasSavedResume}>
          Use Saved Resume
        </label>
        {#if !hasSavedResume}
          <span class="info-icon" title="You must save your resume in the edit/print view first to use this feature">ℹ️</span>
        {/if}
      </div>
      <div>
        <label for="resume-upload">Upload Resume (PDF):</label>
        <input id="resume-upload" type="file" accept=".pdf" required bind:files={resumeFile} disabled={isSubmitting || useSavedResume}>
      </div>
      <div>
        <label for="job-description">Job Description:</label>
        <textarea id="job-description" bind:value={jobDescription} required disabled={isSubmitting}></textarea>
      </div>
      
      <!-- New Tags Section -->
      <div class="tags-section">
        <label for="custom-tags">Custom Instructions:</label>
        <div class="tags-input-container">
          <input 
            id="custom-tags" 
            type="text" 
            placeholder="Add custom instruction (e.g., 'Make all text black')" 
            bind:value={newTag} 
            on:keydown={handleKeydown}
            disabled={isSubmitting}
          >
          <button type="button" class="add-tag-button" on:click={addTag} disabled={isSubmitting}>Add</button>
        </div>
        
        <div class="tags-container">
          {#each tags as tag, index}
            <div class="tag">
              <span>{tag}</span>
              <button type="button" class="remove-tag" on:click={() => removeTag(index)} disabled={isSubmitting}>×</button>
            </div>
          {/each}
        </div>
      </div>
      
      <div class="style-selection">
        <h3>Choose a Path:</h3>
        <div class="style-options">
          <label class="style-option">
            <input type="radio" bind:group={selectedStyle} value="BasicPig" checked>
            <img src="/style_tiles/basicpig.jpg" alt="Basic Pig Style" />
            <span>Executive Pig</span>
          </label>
          <label class="style-option">
            <input type="radio" bind:group={selectedStyle} value="TechHog">
            <img src="/style_tiles/techhog.jpg" alt="Tech Hog Style" />
            <span>Tech Hog</span>
          </label>
          <label class="style-option">
            <input type="radio" bind:group={selectedStyle} value="AndyWarthog">
            <img src="/style_tiles/andywarthog.jpg" alt="Andy Warthog Style" />
            <span>Andy Warthog</span>
          </label>
          <label class="style-option">
            <input type="radio" bind:group={selectedStyle} value="BoardAnimator">
            <img src="/style_tiles/Board Animator.jpg" alt="Boar-d Animator Style" />
            <span>Boar-d Animator</span>
          </label>
        </div>
      </div>
      <button type="submit" disabled={isSubmitting}>
        {#if isSubmitting}
          Generating...
        {:else}
          Generate Resume
        {/if}
      </button>
    </form>

    {#if isSubmitting}
      <div class="loading-animation">
        <div class="loader">
          <div class="circle"></div>
          <div class="circle"></div>
          <div class="circle"></div>
        </div>
        <p>{loadingMessage}</p>
      </div>
    {:else if error}
      <p class="error">{error}</p>
    {:else if generatedResumeHtml}
      <div class="success">
        <p>Resume generated successfully!</p>
        <button on:click={openResumeInNewTab}>View Formatted Resume</button>
      </div>
    {/if}

    {#if showUpgradeModal}
      <div class="modal-backdrop">
        <div class="modal-content">
          <h2>Upgrade Required</h2>
          <p>{error}</p>
          <p>You have reached the limit of your free trial. To continue generating resumes, please upgrade to premium.</p>
          <div class="modal-buttons">
            <button on:click={handleSubscribe}>Upgrade Now</button>
            <button on:click={() => showUpgradeModal = false}>Cancel</button>
          </div>
        </div>
      </div>
    {/if}

    {#if showSkillsAnalyzer}
      <SkillsAnalyzer
        user={user}
        isPremium={isPremium}
        on:close={() => showSkillsAnalyzer = false}
      />
    {/if}
  </main>
{/if}

<footer>
  <div class="footer-links">
    <a href="https://docs.resumehog.com/privacy-policy.html" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
    <a href="https://docs.resumehog.com/terms-of-service.html" target="_blank" rel="noopener noreferrer">Terms of Service</a>
  </div>
</footer>

<style>
  :global(body) {
    background-image: url('/background.webp');
    background-size: auto;
    background-repeat: repeat;
    background-attachment: fixed;
  }

  main {
    padding: 20px;
    background-color: rgba(245, 240, 230, 0.8); /* Soft parchment color */
    border-radius: 10px;
    max-width: 800px;
    margin: 20px auto;
  }

  form {
    display: flex;
    flex-direction: column;
    gap: 15px;
  }

  label {
    font-weight: bold;
  }

  input[type="file"],
  textarea {
    width: 100%;
    padding: 8px;
    border: 1px solid #ccc;
    border-radius: 4px;
  }

  textarea {
    height: 150px;
    resize: vertical;
  }

  button {
    background-color: #2a4b7c; /* Hokusai blue */
    color: #f5f0e6;
    padding: 10px 15px;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-size: 16px;
    position: relative;
    overflow: hidden;
    transition: all 0.3s ease;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
  }

  button:before {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: linear-gradient(
      to right,
      rgba(255, 255, 255, 0) 0%,
      rgba(255, 255, 255, 0.1) 50%,
      rgba(255, 255, 255, 0) 100%
    );
    transform: rotate(30deg);
    animation: shimmer 8s infinite linear;
    pointer-events: none;
  }

  @keyframes shimmer {
    from {
      transform: translateX(-100%) rotate(30deg);
    }
    to {
      transform: translateX(100%) rotate(30deg);
    }
  }

  button:hover {
    background-color: #1a365d; /* Darker Hokusai blue */
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
  }

  button:disabled {
    background-color: #5d7b9d; /* Lighter blue */
    cursor: not-allowed;
    opacity: 0.7;
    box-shadow: none;
  }

  button:disabled:before {
    animation: none;
  }

  .error {
    color: red;
    margin-top: 10px;
  }

  .success {
    color: #2a4b7c; /* Changed from green to Hokusai blue to match palette */
    margin-top: 10px;
  }

  .loading-animation {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-top: 20px;
  }

  .loader {
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .circle {
    width: 20px;
    height: 20px;
    background-color: #2a4b7c; /* Hokusai blue */
    border-radius: 50%;
    margin: 0 10px;
    animation: bounce 0.5s ease-in-out infinite;
  }

  .circle:nth-child(2) {
    animation-delay: 0.1s;
  }

  .circle:nth-child(3) {
    animation-delay: 0.2s;
  }

  @keyframes bounce {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-15px); }
  }

  .loading-animation p {
    margin-top: 20px;
    font-style: italic;
    text-align: center;
    color: #2a4b7c; /* Hokusai blue */
    font-weight: bold;
  }

  .how-it-works {
    background-color: #e8f0f7; /* Light blue background */
    border-radius: 8px;
    padding: 15px;
    margin-bottom: 20px;
  }

  .how-it-works h2 {
    color: #2a4b7c; /* Hokusai blue */
    margin-top: 0;
  }

  .how-it-works ol {
    padding-left: 20px;
  }

  .how-it-works li {
    margin-bottom: 10px;
  }

  footer {
    background-color: rgba(245, 240, 230, 0.8); /* Soft parchment color */
    padding: 15px;
    text-align: center;
    margin-top: 20px;
    border-radius: 10px;
    max-width: 800px;
    margin-left: auto;
    margin-right: auto;
  }

  .footer-links {
    display: flex;
    justify-content: center;
    gap: 20px;
  }

  .footer-links a {
    background-color: #2a4b7c; /* Hokusai blue */
    color: #f5f0e6;
    border: none;
    padding: 8px 15px;
    border-radius: 4px;
    cursor: pointer;
    font-size: 14px;
    transition: all 0.3s ease;
    text-decoration: none;
    position: relative;
    overflow: hidden;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
  }

  .footer-links a:before {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: linear-gradient(
      to right,
      rgba(255, 255, 255, 0) 0%,
      rgba(255, 255, 255, 0.1) 50%,
      rgba(255, 255, 255, 0) 100%
    );
    transform: rotate(30deg);
    animation: shimmer 8s infinite linear;
    pointer-events: none;
  }

  .footer-links a:hover {
    background-color: #1a365d; /* Darker Hokusai blue */
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
  }

  div label {
    display: inline-block;
    margin-right: 20px;
  }

  .style-selection {
    margin-bottom: 30px;
    text-align: center;
  }

  .style-selection h3 {
    font-size: 24px;
    margin-bottom: 20px;
    color: #333;
  }

  .style-options {
    display: flex;
    justify-content: center;
    gap: 40px;
  }

  .style-option {
    display: flex;
    flex-direction: column;
    align-items: center;
    cursor: pointer;
    width: 150px;
    transition: all 0.3s ease;
  }

  .style-option img {
    width: 100%;
    height: auto;
    object-fit: cover;
    border: 3px solid transparent;
    border-radius: 8px;
    transition: all 0.3s ease;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  }

  .style-option input[type="radio"] {
    display: none;
  }

  .style-option input[type="radio"]:checked + img {
    border-color: #d35400; /* Reddish-orange accent from Hokusai prints */
    transform: scale(1.05);
  }

  .style-option span {
    margin-top: 12px;
    font-weight: bold;
    font-size: 16px;
    white-space: nowrap;
  }

  .style-option:hover img {
    transform: scale(1.5);
  }

  @media (max-width: 768px) {
    .style-options {
      flex-direction: column;
      align-items: center;
    }

    .style-option {
      width: 120px;
      margin-bottom: 20px;
    }
  }

  .breathing-glow {
    animation: breathe 3s ease-in-out infinite;
    display: inline-block;
    padding: 0 5px;
    border-radius: 3px;
  }

  @keyframes breathe {
    0%, 100% {
      background-color: rgba(42, 75, 124, 0.2); /* Hokusai blue with transparency */
      box-shadow: 0 0 5px rgba(42, 75, 124, 0.5);
    }
    50% {
      background-color: rgba(42, 75, 124, 0.5);
      box-shadow: 0 0 15px rgba(42, 75, 124, 0.8);
    }
  }

  .modal-backdrop {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0,0,0,0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 9999;
  }

  .modal-content {
    background: #fff;
    border-radius: 8px;
    padding: 20px;
    max-width: 400px;
    width: 90%;
    text-align: center;
  }

  .modal-buttons {
    margin-top: 20px;
    display: flex;
    justify-content: space-evenly;
  }

  .modal-buttons button {
    background-color: #2a4b7c; /* Hokusai blue */
    border: none;
    padding: 10px 15px;
    color: #f5f0e6;
    border-radius: 4px;
    cursor: pointer;
    position: relative;
    overflow: hidden;
    transition: all 0.3s ease;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
  }

  .modal-buttons button:before {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: linear-gradient(
      to right,
      rgba(255, 255, 255, 0) 0%,
      rgba(255, 255, 255, 0.1) 50%,
      rgba(255, 255, 255, 0) 100%
    );
    transform: rotate(30deg);
    animation: shimmer 8s infinite linear;
    pointer-events: none;
  }

  .modal-buttons button:hover {
    background-color: #1a365d; /* Darker Hokusai blue */
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
  }

  .user-controls {
    position: absolute;
    top: 20px;
    right: 20px;
    display: flex;
    align-items: center;
    gap: 15px;
    background-color: rgba(245, 240, 230, 0.9); /* Soft parchment color */
    padding: 10px;
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  }

  @media (max-width: 768px) {
    .user-controls {
      position: relative;
      top: 0;
      right: 0;
      flex-direction: column;
      align-items: stretch;
      margin-bottom: 20px;
    }
  }

  .top-nav {
    display: flex;
    justify-content: space-between;
    align-items: center;
    background-color: rgba(245, 240, 230, 0.8); /* Soft parchment color */
    padding: 10px 20px;
    max-width: 800px;
    margin: 20px auto;
    border-radius: 10px;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  }

  .user-controls {
    display: flex;
    align-items: center;
  }

  .profile-container {
    position: relative;
  }

  .profile-button {
    background-color: #2a4b7c; /* Hokusai blue */
    color: #f5f0e6;
    border: none;
    padding: 8px 12px;
    border-radius: 4px;
    cursor: pointer;
    position: relative;
    overflow: hidden;
    transition: all 0.3s ease;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
  }

  .profile-button:before {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: linear-gradient(
      to right,
      rgba(255, 255, 255, 0) 0%,
      rgba(255, 255, 255, 0.1) 50%,
      rgba(255, 255, 255, 0) 100%
    );
    transform: rotate(30deg);
    animation: shimmer 8s infinite linear;
    pointer-events: none;
  }

  .profile-button:hover {
    background-color: #1a365d; /* Darker Hokusai blue */
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
  }

  .dropdown-content {
    position: absolute;
    right: 0;
    top: 40px;
    background-color: #f5f0e6; /* Soft parchment color */
    min-width: 160px;
    border: 1px solid #d9c8b4; /* Light brown border */
    border-radius: 4px;
    z-index: 999;
    box-shadow: 0 2px 6px rgba(0,0,0,0.15);
    padding: 10px;
  }

  .dropdown-content button {
    display: block;
    width: 100%;
    background: none;
    border: none;
    text-align: left;
    cursor: pointer;
    padding: 8px 12px;
    color: #333;
    font-size: 14px;
    transition: all 0.2s ease;
    border-radius: 2px;
    position: relative;
    overflow: hidden;
  }

  .dropdown-content button:hover {
    background-color: #e8f0f7; /* Light blue background */
    color: #2a4b7c; /* Hokusai blue */
  }

  .tags-section {
    margin-bottom: 20px;
  }
  
  .tags-input-container {
    display: flex;
    gap: 10px;
    margin-bottom: 10px;
  }
  
  .tags-input-container input {
    flex-grow: 1;
    padding: 8px;
    border: 1px solid #ccc;
    border-radius: 4px;
  }
  
  .add-tag-button {
    background-color: #2a4b7c;
    color: white;
    border: none;
    border-radius: 4px;
    padding: 8px 15px;
    cursor: pointer;
  }
  
  .tags-container {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    margin-top: 10px;
  }
  
  .tag {
    display: flex;
    align-items: center;
    background-color: #e8f0f7;
    border-radius: 16px;
    padding: 5px 10px;
    font-size: 14px;
  }
  
  .remove-tag {
    background: none;
    border: none;
    color: #2a4b7c;
    margin-left: 5px;
    cursor: pointer;
    font-size: 16px;
    font-weight: bold;
    padding: 0 5px;
  }
  
  .remove-tag:hover {
    color: #d35400;
  }

  .toggle-label {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .info-icon {
    cursor: help;
    color: #2a4b7c;
  }

  .action-buttons-extra {
      display: flex;
      justify-content: center;
      margin-bottom: 20px;
  }

  .skills-analyzer-button {
    background-color: #e67e22;
    color: white;
    padding: 10px 20px;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-size: 16px;
    font-weight: bold;
    position: relative;
    overflow: hidden;
    transition: all 0.3s ease;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
  }

    .skills-analyzer-button:disabled {
        background-color: #bdc3c7;
        color: #7f8c8d;
        cursor: not-allowed;
        box-shadow: none;
    }
    .skills-analyzer-button:disabled:hover {
         background-color: #bdc3c7;
    }
    .skills-analyzer-button:not(:disabled):hover {
        background-color: #d35400;
         box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
    }

    .premium-lock {
        margin-left: 5px;
        font-size: 0.8em;
        opacity: 0.7;
    }

    .skills-analyzer-button:disabled::before {
        display: none;
    }
</style>



<!-- cherry checkpoint: skills analyzer ready for production -->
